/**
 * Stakeholder CRM Utility Functions
 * Copied from src/lib/crm/utils.ts for use in Stakeholder Management Page
 */

import { formatDistance, parseISO, differenceInDays, format } from 'date-fns';
import { vi } from 'date-fns/locale';
import type { InteractionType, CRMStakeholder, CRMInteraction, CRMFollowUp } from '@/lib/crm/types';

/**
 * Get color class for interaction type badge
 */
export function getInteractionTypeColor(type: InteractionType): string {
  const colors: Record<InteractionType, string> = {
    'Meeting': 'text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-950 dark:border-blue-800',
    'Call': 'text-green-700 bg-green-50 border-green-200 dark:text-green-300 dark:bg-green-950 dark:border-green-800',
    'Site Visit': 'text-orange-700 bg-orange-50 border-orange-200 dark:text-orange-300 dark:bg-orange-950 dark:border-orange-800',
    'Delivery': 'text-purple-700 bg-purple-50 border-purple-200 dark:text-purple-300 dark:bg-purple-950 dark:border-purple-800',
    'Approval': 'text-teal-700 bg-teal-50 border-teal-200 dark:text-teal-300 dark:bg-teal-950 dark:border-teal-800',
  };
  return colors[type] || 'text-gray-700 bg-gray-50';
}

/**
 * Get color class for event type (follow-up or interaction)
 */
export function getEventTypeColor(eventType: 'follow-up' | 'interaction', interactionType?: InteractionType): string {
  if (eventType === 'follow-up') {
    return 'text-orange-700 bg-orange-50 border-orange-200';
  }
  return interactionType ? getInteractionTypeColor(interactionType) : 'text-gray-700 bg-gray-50';
}

/**
 * Get badge variant for follow-up date
 */
export function getFollowUpBadgeVariant(dateStr?: string): 'destructive' | 'default' | 'secondary' | 'outline' {
  if (!dateStr) return 'outline';
  
  const date = parseISO(dateStr);
  const daysUntil = differenceInDays(date, new Date());
  
  if (daysUntil < 0) return 'destructive'; // Overdue
  if (daysUntil <= 2) return 'default'; // Within 2 days
  if (daysUntil <= 7) return 'secondary'; // Within a week
  return 'outline'; // More than a week
}

/**
 * Calculate activity status based on last contact date
 */
export function getActivityStatus(lastContact?: string): {
  label: string;
  color: string;
  progress: number;
} {
  if (!lastContact) {
    return { label: 'Chưa liên lạc', color: 'text-gray-500', progress: 0 };
  }

  const daysSince = differenceInDays(new Date(), parseISO(lastContact));
  
  if (daysSince < 7) {
    return { label: 'Hoạt động cao', color: 'text-green-600', progress: 100 };
  } else if (daysSince < 30) {
    return { label: 'Hoạt động trung bình', color: 'text-yellow-600', progress: 50 };
  } else {
    return { label: 'Ít hoạt động', color: 'text-red-600', progress: 20 };
  }
}

/**
 * Format date to relative time in Vietnamese
 */
export function formatRelativeTime(dateStr: string): string {
  try {
    return formatDistance(parseISO(dateStr), new Date(), { locale: vi, addSuffix: true });
  } catch {
    return dateStr;
  }
}

/**
 * Format date to dd/MM/yyyy
 */
export function formatDateDisplay(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: vi });
  } catch {
    return dateStr;
  }
}

/**
 * Format date to dd/MM/yyyy HH:mm
 */
export function formatDateTimeDisplay(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy HH:mm', { locale: vi });
  } catch {
    return dateStr;
  }
}

/**
 * Get initials from name
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Calculate dashboard metrics
 */
export function calculateMetrics(
  stakeholders: CRMStakeholder[],
  interactions: CRMInteraction[],
  followUps: CRMFollowUp[]
) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  // Overdue follow-ups
  const overdueFollowUps = stakeholders.filter(s => {
    if (!s.nextFollowUp) return false;
    return parseISO(s.nextFollowUp) < today;
  }).length;

  // Today's follow-ups
  const todayFollowUps = stakeholders.filter(s => {
    if (!s.nextFollowUp) return false;
    const followUpDate = parseISO(s.nextFollowUp);
    return followUpDate.toDateString() === today.toDateString();
  }).length;

  // This week's interactions
  const weekInteractions = interactions.filter(i => {
    return parseISO(i.date) >= weekAgo;
  }).length;

  // Follow-up completion rate
  const completedFollowUps = followUps.filter(f => f.status === 'completed').length;
  const totalFollowUps = followUps.length;
  const followUpCompletionRate = totalFollowUps > 0 
    ? Math.round((completedFollowUps / totalFollowUps) * 100) 
    : 0;

  // Stakeholders needing attention (no interaction in 30 days)
  const stakeholderIds = new Set(
    interactions
      .filter(i => parseISO(i.date) >= thirtyDaysAgo)
      .map(i => i.stakeholder_id)
  );
  const inactiveStakeholders = stakeholders.filter(s => !stakeholderIds.has(s.id)).length;

  return {
    totalStakeholders: stakeholders.length,
    overdueFollowUps,
    todayFollowUps,
    weekInteractions,
    followUpCompletionRate,
    inactiveStakeholders,
  };
}

/**
 * Get role distribution for pie chart
 */
export function getRoleDistribution(stakeholders: CRMStakeholder[]) {
  const roleCounts: Record<string, number> = {};
  
  stakeholders.forEach(s => {
    const role = s.role || 'Khác';
    roleCounts[role] = (roleCounts[role] || 0) + 1;
  });

  return Object.entries(roleCounts).map(([name, value]) => ({ name, value }));
}

/**
 * Get interaction distribution for bar chart
 */
export function getInteractionDistribution(interactions: CRMInteraction[]) {
  const typeCounts: Record<string, number> = {};
  
  interactions.forEach(i => {
    typeCounts[i.type] = (typeCounts[i.type] || 0) + 1;
  });

  return Object.entries(typeCounts).map(([name, value]) => ({ name, value }));
}
